import type { ReactNode } from 'react'

export type KeyValueRowTone = 'default' | 'ok' | 'warn' | 'bad' | 'muted'

export interface KeyValueRowProps {
  /** Label on the left */
  label: ReactNode
  /** Value on the right */
  value: ReactNode
  /** Optional secondary hint under label */
  sublabel?: ReactNode
  /** When true, formats value with --font-figures and tabular-nums */
  numeric?: boolean
  /** Value color tone */
  tone?: KeyValueRowTone
  className?: string
  testId?: string
}

const TONE_CLASSES: Record<KeyValueRowTone, string> = {
  default: 'text-[var(--text-strong)]',
  ok: 'text-[var(--success)]',
  warn: 'text-[var(--warning)]',
  bad: 'text-[var(--danger)]',
  muted: 'text-[var(--muted)]',
}

/**
 * KeyValueRow — dense two-column key-value row for ledger cards and stats panels.
 * Renders label on left and numeric/text value on right with tabular --font-figures.
 */
export function KeyValueRow({
  label,
  value,
  sublabel,
  numeric,
  tone = 'default',
  className,
  testId,
}: KeyValueRowProps) {
  const isNumeric = numeric ?? (typeof value === 'number' || (typeof value === 'string' && /^[0-9₹$€%+\-.,/ ]+$/.test(value)))
  const toneClass = TONE_CLASSES[tone] ?? TONE_CLASSES.default

  return (
    <div
      data-testid={testId}
      className={`flex items-center justify-between gap-4 px-3.5 py-2 border-b border-[var(--border)] text-xs select-none ${className ?? ''}`}
    >
      <div className="flex flex-col min-w-0">
        <span className="text-[var(--muted)] truncate">{label}</span>
        {sublabel ? (
          <span className="text-[10px] text-[var(--muted)] opacity-80 mt-0.5 truncate">{sublabel}</span>
        ) : null}
      </div>

      <div
        className={`flex-none font-semibold text-right ${toneClass}`}
        style={isNumeric ? { fontFamily: 'var(--font-figures)', fontVariantNumeric: 'tabular-nums' } : undefined}
      >
        {value}
      </div>
    </div>
  )
}
